import { createLogger } from '@/lib/logger'

import {
  AttachmentItem,
  DocumentAttachmentResponse,
  DocumentCategory,
  DocumentProtectionResponse,
  DocumentResponse,
  ModuleDocumentResponse,
  ModuleDocumentsQuery,
  ModuleDocumentsSummary,
  NestedDocumentsQuery,
  NestedDocumentsResponse,
  PaginatedResponse,
  UpdateDocumentProtectionDTO,
} from '@scrambl/shared'

/**
 * Documents API service for managing file uploads and document categories.
 * @module lib/api/documents.api
 */

import { BaseApi } from './base-api'
import { getCurrentClientPortalContext } from './client-portal-context'
import { clientPortalApi } from './client-portal.api'
import { createSingletonApi } from './singleton-factory'

const logger = createLogger('lib/api/documents.api')

export type UploadProgressCallback = (progress: number) => void
interface CreateLinkData {
  moduleType: string
  moduleId: string
  url: string
  displayName: string
  categoryId?: string
  tags?: string[]
  metadata?: Record<string, unknown>
  documentDate?: Date
}

interface DocumentQueryParams {
  search?: string
  moduleType?: string
  moduleId?: string
  categoryId?: string
  type?: string
  isPinned?: boolean
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  excludeModuleType?: string
  excludeModuleId?: string
}

interface DocumentsResponse {
  documents: DocumentResponse[]
  total: number
}

type DocumentWithCustomFields = DocumentResponse & {
  customFields?: Record<string, unknown>
}

const flattenDocumentCustomFields = <T extends DocumentWithCustomFields>(
  document: T,
): T => {
  const result = { ...document }

  if (result.customFields && typeof result.customFields === 'object') {
    const resultRecord = result as Record<string, unknown>
    Object.entries(result.customFields).forEach(([fieldName, value]) => {
      resultRecord[`custom_${fieldName}`] = value
    })
  }

  return result
}

class DocumentsApiService extends BaseApi<
  DocumentResponse,
  never,
  Partial<DocumentResponse>
> {
  constructor() {
    super({
      baseEndpoint: '/documents',
      responseNormalization: true,
    })
  }

  private getClientPortalEntityId(): string | null {
    return getCurrentClientPortalContext()?.entityId ?? null
  }

  // Get documents for a module
  async getDocuments(
    moduleType: string,
    moduleId: string,
  ): Promise<DocumentResponse[]> {
    const portalEntityId = this.getClientPortalEntityId()
    const supportedPortalModules = new Set(['note', 'thread', 'thread_comment'])
    if (
      portalEntityId &&
      supportedPortalModules.has(moduleType.toLowerCase())
    ) {
      const documents = await clientPortalApi.listDocumentsByModule(
        portalEntityId,
        moduleType,
        moduleId,
      )
      return documents.map(document =>
        flattenDocumentCustomFields(document as DocumentWithCustomFields),
      )
    }

    const documents = await this.makeRequest<DocumentResponse[]>(
      `${this.config.baseEndpoint}/by-module/${moduleType}/${moduleId}`,
    )
    return documents.map(document =>
      flattenDocumentCustomFields(document as DocumentWithCustomFields),
    )
  }

  /**
   * One page of a record's Files table — owned and attached files together,
   * paged, sorted, searched and folder-filtered on the server.
   */
  async getModuleDocumentsPage(
    moduleType: string,
    moduleId: string,
    params: ModuleDocumentsQuery = {},
  ): Promise<PaginatedResponse<ModuleDocumentResponse>> {
    const queryParams = new URLSearchParams()
    queryParams.append('page', String(params.page ?? 1))
    if (params.limit) queryParams.append('limit', String(params.limit))
    const search = params.search?.trim()
    if (search) queryParams.append('search', search)
    if (params.categoryIds?.length)
      queryParams.append('categoryIds', params.categoryIds.join(','))
    if (params.excludeCategoryIds?.length)
      queryParams.append(
        'excludeCategoryIds',
        params.excludeCategoryIds.join(','),
      )
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    const response = await this.makeRequest<
      PaginatedResponse<ModuleDocumentResponse>
    >(
      `${this.config.baseEndpoint}/by-module/${moduleType.toLowerCase()}/${moduleId}?${queryParams.toString()}`,
    )
    return {
      ...response,
      data: response.data.map(document =>
        flattenDocumentCustomFields(document as ModuleDocumentResponse),
      ),
    }
  }

  /** Counts behind a record's Files table, independent of the loaded page. */
  async getModuleDocumentsSummary(
    moduleType: string,
    moduleId: string,
  ): Promise<ModuleDocumentsSummary> {
    return this.makeRequest<ModuleDocumentsSummary>(
      `${this.config.baseEndpoint}/by-module/${moduleType.toLowerCase()}/${moduleId}/summary`,
    )
  }

  /**
   * Files owned by records further down this record's chain — a client's
   * projects and tasks, a project's tasks, a task's subtasks — each carrying
   * the lineage that leads to it.
   */
  async getNestedDocuments(
    moduleType: string,
    moduleId: string,
    params: NestedDocumentsQuery = {},
  ): Promise<NestedDocumentsResponse> {
    const queryParams = new URLSearchParams()
    const search = params.search?.trim()
    if (search) queryParams.append('search', search)
    if (params.limit !== undefined)
      queryParams.append('limit', String(params.limit))
    if (params.offset) queryParams.append('offset', String(params.offset))
    if (params.source) queryParams.append('source', params.source)
    const queryString = queryParams.toString()
    const response = await this.makeRequest<NestedDocumentsResponse>(
      `${this.config.baseEndpoint}/by-module/${moduleType.toLowerCase()}/${moduleId}/nested${queryString ? `?${queryString}` : ''}`,
    )
    return {
      ...response,
      documents: response.documents.map(document =>
        flattenDocumentCustomFields(document),
      ),
    }
  }

  // List documents across organization with filters
  async getDocumentsList(
    params?: DocumentQueryParams,
  ): Promise<DocumentsResponse> {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.moduleType) queryParams.append('moduleType', params.moduleType)
    if (params?.moduleId) queryParams.append('moduleId', params.moduleId)
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId)
    if (params?.type) queryParams.append('type', params.type)
    if (params?.isPinned !== undefined)
      queryParams.append('isPinned', String(params.isPinned))
    if (params?.page) queryParams.append('page', String(params.page))
    if (params?.limit) queryParams.append('limit', String(params.limit))
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    if (params?.excludeModuleType)
      queryParams.append('excludeModuleType', params.excludeModuleType)
    if (params?.excludeModuleId)
      queryParams.append('excludeModuleId', params.excludeModuleId)

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    const response = await this.makeRequest<DocumentsResponse>(
      `${this.config.baseEndpoint}${queryString}`,
    )
    return {
      ...response,
      documents: response.documents.map(document =>
        flattenDocumentCustomFields(document as DocumentWithCustomFields),
      ),
    }
  }

  async getDocument(documentId: string): Promise<DocumentResponse> {
    const document = await this.findById(documentId)
    return flattenDocumentCustomFields(document as DocumentWithCustomFields)
  }

  async getModuleCounts(): Promise<{ allDocuments: number }> {
    return this.makeRequest<{ allDocuments: number }>(
      `${this.config.baseEndpoint}/module-counts`,
    )
  }

  // Get signed URL for a document
  async getDocumentSignedUrl(
    documentId: string,
  ): Promise<{ url: string; thumbnailUrl?: string }> {
    if (!documentId) throw new Error('Document ID is required')

    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getDocumentSignedUrl(portalEntityId, documentId)
    }

    return this.makeRequest<{ url: string; thumbnailUrl?: string }>(
      `${this.config.baseEndpoint}/${documentId}/signed-url`,
    )
  }

  // File upload
  async uploadFile(
    moduleType: string,
    moduleId: string,
    file: File,
    onProgress?: UploadProgressCallback,
    categoryId?: string,
    customFieldId?: string,
    options: { signal?: AbortSignal; portalEntityId?: string | null } = {},
  ): Promise<DocumentResponse> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    const supportedPortalModules = new Set(['note', 'thread', 'thread_comment'])
    if (
      portalEntityId &&
      supportedPortalModules.has(moduleType.toLowerCase())
    ) {
      return clientPortalApi.uploadFile(
        portalEntityId,
        moduleType,
        moduleId,
        file,
        false,
        false,
        { signal: options.signal },
      ) as Promise<DocumentResponse>
    }

    const formData = new FormData()
    formData.append('file', file)
    if (categoryId) formData.append('categoryId', categoryId)

    if (onProgress) {
      logger.warn(
        'Progress tracking not supported with current upload implementation',
      )
      onProgress(0)
    }

    const queryParams = customFieldId ? `?customFieldId=${customFieldId}` : ''
    const response = await this.makeRequest<DocumentResponse>(
      `/files/upload/${moduleType}/${moduleId}${queryParams}`,
      {
        method: 'POST',
        body: formData,
        skipJsonParse: false,
        signal: options.signal,
      },
    )

    if (onProgress) onProgress(100)
    return response
  }

  // Upload file for attachments custom field - returns AttachmentItem for inline storage
  async uploadAttachment(
    moduleType: string,
    moduleId: string,
    file: File,
    customFieldId: string,
    onProgress?: UploadProgressCallback,
  ): Promise<AttachmentItem> {
    const formData = new FormData()
    formData.append('file', file)

    if (onProgress) {
      logger.warn(
        'Progress tracking not supported with current upload implementation',
      )
      onProgress(0)
    }

    const response = await this.makeRequest<{
      documentId: string
      id?: string
      fileName: string
      size?: number
      fileSize?: number
      contentType: string
    }>(
      `/files/upload/${moduleType}/${moduleId}?customFieldId=${customFieldId}`,
      {
        method: 'POST',
        body: formData,
        skipJsonParse: false,
      },
    )

    if (onProgress) onProgress(100)

    return {
      documentId: response.documentId || response.id || '',
      fileName: response.fileName,
      fileSize: response.size ?? response.fileSize ?? 0,
      contentType: response.contentType,
    }
  }

  // Multiple file upload
  async uploadMultipleFiles(
    moduleType: string,
    moduleId: string,
    files: File[],
    onProgress?: (fileIndex: number, progress: number) => void,
    categoryId?: string,
  ): Promise<DocumentResponse[]> {
    const results: DocumentResponse[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!file) continue
      const result = await this.uploadFile(
        moduleType,
        moduleId,
        file,
        progress => onProgress?.(i, progress),
        categoryId,
      )
      results.push(result)
    }
    return results
  }

  // Create link document
  async createLink(data: CreateLinkData): Promise<DocumentResponse> {
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/link`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  // Category operations
  async getCategories(
    moduleType?: string,
    moduleId?: string,
  ): Promise<DocumentCategory[]> {
    const endpoint =
      moduleType && moduleId
        ? `${this.config.baseEndpoint}/categories/${moduleType}/${moduleId}`
        : `${this.config.baseEndpoint}/categories`
    return this.makeRequest<DocumentCategory[]>(endpoint)
  }

  async createCategory(data: {
    moduleType?: string
    moduleId?: string
    name: string
    description?: string
    color?: string
    parentId?: string
  }): Promise<DocumentCategory> {
    return this.makeRequest<DocumentCategory>(
      `${this.config.baseEndpoint}/categories`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async updateCategory(
    categoryId: string,
    data: { name?: string; description?: string; color?: string },
  ): Promise<DocumentCategory> {
    return this.makeRequest<DocumentCategory>(
      `${this.config.baseEndpoint}/categories/${categoryId}`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  }

  async deleteCategory(categoryId: string): Promise<void> {
    await this.makeRequest(
      `${this.config.baseEndpoint}/categories/${categoryId}`,
      { method: 'DELETE' },
    )
  }

  // Protection (access-restricted folders & files)
  async getDocumentProtection(
    documentId: string,
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/${documentId}/protection`,
    )
  }

  async setDocumentProtection(
    documentId: string,
    data: UpdateDocumentProtectionDTO,
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/${documentId}/protection`,
      { method: 'PATCH', body: data },
    )
  }

  async updateDocumentAccessList(
    documentId: string,
    userIds: string[],
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/${documentId}/protection/members`,
      { method: 'PUT', body: { userIds } },
    )
  }

  async getCategoryProtection(
    categoryId: string,
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/categories/${categoryId}/protection`,
    )
  }

  async setCategoryProtection(
    categoryId: string,
    data: UpdateDocumentProtectionDTO,
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/categories/${categoryId}/protection`,
      { method: 'PATCH', body: data },
    )
  }

  async updateCategoryAccessList(
    categoryId: string,
    userIds: string[],
  ): Promise<DocumentProtectionResponse> {
    return this.makeRequest<DocumentProtectionResponse>(
      `${this.config.baseEndpoint}/categories/${categoryId}/protection/members`,
      { method: 'PUT', body: { userIds } },
    )
  }

  // Document operations
  async assignDocumentToCategory(
    documentId: string,
    categoryId: string | null,
    moduleType: string,
    moduleId: string,
  ): Promise<DocumentResponse> {
    if (!documentId) throw new Error('Document ID is required')
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}/category`,
      {
        method: 'PATCH',
        body: { categoryId, moduleType, moduleId },
        withAuth: true,
        skipDeduplication: true,
      },
    )
  }

  async updateDocument(
    documentId: string,
    data: Record<string, unknown>,
  ): Promise<DocumentResponse> {
    const response = await this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}`,
      {
        method: 'PATCH',
        body: data,
      },
    )
    return flattenDocumentCustomFields(response as DocumentWithCustomFields)
  }

  async updateLinkUrl(
    documentId: string,
    data: { url: string },
  ): Promise<DocumentResponse> {
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}/link`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  }

  async replaceFile(documentId: string, file: File): Promise<DocumentResponse> {
    const formData = new FormData()
    formData.append('file', file)
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}/replace`,
      {
        method: 'POST',
        body: formData,
        skipJsonParse: false,
      },
    )
  }

  async deleteDocument(documentId: string): Promise<void> {
    await this.makeRequest(`${this.config.baseEndpoint}/${documentId}`, {
      method: 'DELETE',
    })
  }

  // Versioning
  async getVersions(documentId: string): Promise<
    Array<{
      id: string
      version: number
      createdAt: string
      displayName?: string
      url?: string
      fileName?: string
    }>
  > {
    return this.makeRequest(
      `${this.config.baseEndpoint}/${documentId}/versions`,
    )
  }

  async restoreVersion(
    documentId: string,
    versionId: string,
  ): Promise<DocumentResponse> {
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}/versions/${versionId}/restore`,
      {
        method: 'POST',
      },
    )
  }

  // Reordering and moving
  async reorderDocuments(
    moduleType: string,
    moduleId: string,
    documentIds: string[],
  ): Promise<void> {
    await this.makeRequest(
      `${this.config.baseEndpoint}/reorder/${moduleType}/${moduleId}`,
      {
        method: 'PATCH',
        body: { documentIds },
      },
    )
  }

  async moveDocument(
    documentId: string,
    targetCategoryId: string | null,
    moduleType: string,
    moduleId: string,
    targetIndex?: number,
  ): Promise<DocumentResponse> {
    return this.makeRequest<DocumentResponse>(
      `${this.config.baseEndpoint}/${documentId}/move`,
      {
        method: 'PATCH',
        body: {
          categoryId: targetCategoryId,
          moduleType,
          moduleId,
          targetIndex,
        },
        withAuth: true,
        skipDeduplication: true,
      },
    )
  }

  // ==================
  // Document Attachments
  // ==================

  // Get attached documents for a module
  async getAttachedDocuments(
    moduleType: string,
    moduleId: string,
  ): Promise<DocumentAttachmentResponse[]> {
    return this.makeRequest<DocumentAttachmentResponse[]>(
      `${this.config.baseEndpoint}/attachments/${moduleType}/${moduleId}`,
    )
  }

  // Search documents for attachment (uses main documents list with exclude params)
  async searchDocumentsForAttachment(params: {
    search?: string
    excludeModuleType?: string
    excludeModuleId?: string
    page?: number
    limit?: number
  }): Promise<{ documents: DocumentResponse[]; total: number }> {
    return this.getDocumentsList({
      search: params.search,
      excludeModuleType: params.excludeModuleType,
      excludeModuleId: params.excludeModuleId,
      page: params.page,
      limit: params.limit,
    })
  }

  // Attach existing documents to a module
  async attachDocuments(
    documentIds: string[],
    moduleType: string,
    moduleId: string,
  ): Promise<DocumentAttachmentResponse[]> {
    return this.makeRequest<DocumentAttachmentResponse[]>(
      `${this.config.baseEndpoint}/attachments`,
      {
        method: 'POST',
        body: { documentIds, moduleType, moduleId },
      },
    )
  }

  // Detach a document from a module
  async detachDocument(
    documentId: string,
    moduleType: string,
    moduleId: string,
  ): Promise<void> {
    await this.makeRequest(
      `${this.config.baseEndpoint}/attachments/${moduleType}/${moduleId}/${documentId}`,
      { method: 'DELETE' },
    )
  }
}

// Export singleton instance
export const documentsApi = createSingletonApi(DocumentsApiService)
